import * as React from 'react';

import {
  ISuggestion,
  EGender,
  TSetInputValue,
  TSetSuggestFiltered,
  TSetSuggestPart,
  TSetGender,
  TSetPlaceholder,
  TResetSuggested,
  TGetFIOSuggest,
} from '../../../types/fio';
import { getPlaceholderText, placeholders } from './getPlaceholderText';
import { setSuggest } from './setSuggest';
import { getCurrentPartFIO } from './getCurrentPartFIO';
import { getQueryIndex } from './getQueryIndex';
import { getFillProgress } from './getFillProgress';

export interface IUseOnChangeHandlerParams {
  suggestFiltered: ISuggestion[];
  gender: EGender;
  setInputValue: TSetInputValue;
  setSuggestFiltered: TSetSuggestFiltered;
  setSuggestPart: TSetSuggestPart;
  setGender: TSetGender;
  setPlaceholder: TSetPlaceholder;
  resetSuggested: TResetSuggested;
  getFIOSuggest: TGetFIOSuggest;
}

export function useOnChangeHandler({
  suggestFiltered,
  setSuggestFiltered,
  gender,
  setGender,
  setSuggestPart,
  setPlaceholder,
  resetSuggested,
  setInputValue,
  getFIOSuggest,
}: IUseOnChangeHandlerParams) {
  return React.useCallback(
    (inputValue: string) => {
      if (inputValue === ' ') {
        return;
      }
      setInputValue(inputValue);

      const inputSplitValue = inputValue.split(' ');
      const inputSplitValueClear = inputSplitValue.filter(Boolean);
      const { isAfterSurnameSpaceSymbol, isAfterNameSpaceSymbol, isFIOFinished } = getFillProgress(inputSplitValue);

      /**
       * Если ФИО инпут заполнен (есть минимум 2 пробела и между ними 3 текстовых элемента)
       * сбрасываем все саджесты
       */
      if (isFIOFinished) {
        resetSuggested();

        return;
      }

      const placeholder = getPlaceholderText(inputSplitValue);
      setPlaceholder(placeholder);

      /**
       * Если инпут стал пустым - сбрасываем все саджесты, ставим дофолтный плейсхолдер и гендер
       */
      if (!inputSplitValueClear.length) {
        resetSuggested();
        setGender(EGender.UNKNOWN);
        setPlaceholder(placeholders.default);

        return;
      }

      /**
       * Когда есть только фамилия (без пробела в конце, то есть грубо говоря фамилию ввели не доконца)
       * скидываем гендер к неопределенному, чтобы показать все возможные варианты фамилий
       */
      if (inputSplitValueClear.length === 1) {
        setGender(EGender.UNKNOWN);
      }

      /**
       * Если поставили пробел 1 раз (после фамилии) или 2 раза (после имени)
       * пытаемся определить гендер по фамилии если 1 пробел и по имени если 2-й пробел
       */
      if (isAfterSurnameSpaceSymbol || isAfterNameSpaceSymbol) {
        resetSuggested();
        const index = isAfterSurnameSpaceSymbol ? 0 : 1;
        const value = suggestFiltered.find(item => item.value.toLowerCase() === inputSplitValue[index].toLowerCase());

        if (value) {
          setGender(value.data.gender);
        }

        return;
      }

      const currentPartFIO = getCurrentPartFIO(inputSplitValueClear);
      const queryIndex = getQueryIndex(currentPartFIO);
      const eqInputAndSuggest = suggestFiltered.find(item => item.value.indexOf(inputSplitValue[queryIndex]) === 0);

      /**
       * Если пользователь продолжает вводить то что уже есть в саджесте
       *  обновляем подскажку внутри инпута
       *  не перезапрашиваем саджест
       */
      if (eqInputAndSuggest) {
        setSuggestPart(eqInputAndSuggest.value.substring(inputSplitValue[queryIndex].length));

        return;
      }

      setSuggest({
        inputSplitValue,
        currentPartFIO,
        queryIndex,
        gender,
        setSuggestFiltered,
        setSuggestPart,
        getFIOSuggest,
      });
    },
    [
      setInputValue,
      setPlaceholder,
      gender,
      resetSuggested,
      setGender,
      setSuggestFiltered,
      suggestFiltered,
      setSuggestPart,
      getFIOSuggest,
    ],
  );
}
